import { useState, useCallback, useMemo, useEffect } from 'react'
import { ParameterBar } from '@/components/forms'
import { ChartModal } from '@/components/modals'
import { ReturnsTable } from '@/components/tables/ReturnsTable'
import { useCalculation } from '@/hooks/useCalculation'
import { useUserState } from '@/hooks/useUserState'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { useAutoCalculate } from '@/hooks/useAutoCalculate'
import { useGroupOrder } from '@/hooks/useGroupOrder'

// Default date: 5 years ago to today
function getDefaultStartDate(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 5)
  return date.toISOString().split('T')[0]
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0]
}

interface CalculatorSettings {
  initialInvestment: number
  adjustInflation: boolean
  groupByUnderlying: boolean
}

const DEFAULT_SETTINGS: CalculatorSettings = {
  initialInvestment: 10000,
  adjustInflation: false,
  groupByUnderlying: false,
}

/**
 * Main Calculator page component
 * Compact layout with ParameterBar, ReturnsTable, and ChartModal
 * Auto-calculates when tickers are added or parameters change
 */
export function Calculator() {
  // Persisted state (synced to Supabase for authenticated users)
  const {
    tickers,
    setTickers,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    settings,
    setSettings,
    visibleColumns,
    setVisibleColumns,
    groupOrder,
    setGroupOrder,
  } = useUserState({ debounceMs: 1000 })

  // Column visibility with external state from useUserState
  const {
    toggleColumn,
    toggleAll,
    allColumns,
  } = useColumnVisibility({
    visibleColumns,
    setVisibleColumns,
  })

  // Column group ordering with external state from useUserState
  const { reorderGroups } = useGroupOrder({
    groupOrder,
    setGroupOrder,
  })

  // Modal state
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null)
  const [isChartModalOpen, setIsChartModalOpen] = useState(false)

  // Calculation hook
  const { loading, loadingTickers, error, data, calculateSingle, calculateBatch, removeTickerData, reset } = useCalculation()

  // Perform calculation - uses batch endpoint for efficiency
  const doCalculate = useCallback(() => {
    if (tickers.length === 0) return

    // Clear existing data when parameters change (new date range = new data)
    reset()

    // Use batch endpoint to send all tickers in one request
    calculateBatch({
      tickers,
      startDate,
      endDate,
      reinvestDividends: true,
      adjustInflation: settings.adjustInflation,
      initialInvestment: settings.initialInvestment,
    })
  }, [tickers, startDate, endDate, settings, calculateBatch, reset])

  // Auto-calculate when parameters change (debounced)
  useAutoCalculate(
    {
      tickers,
      startDate,
      endDate,
      initialInvestment: settings.initialInvestment,
      adjustInflation: settings.adjustInflation,
      enabled: tickers.length > 0,
      debounceMs: 600,
    },
    doCalculate
  )

  // Calculate on initial load if tickers exist - use batch endpoint for efficiency
  useEffect(() => {
    if (tickers.length > 0 && !data && !loading) {
      // Use batch endpoint to send all tickers in one request
      calculateBatch({
        tickers,
        startDate,
        endDate,
        reinvestDividends: true,
        adjustInflation: settings.adjustInflation,
        initialInvestment: settings.initialInvestment,
      })
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When grouping is enabled, auto-add missing underlying tickers
  useEffect(() => {
    if (!settings.groupByUnderlying || !data?.tableData) return

    // Get unique underlying tickers from current data (excluding tickers that are their own underlying)
    const underlyings = new Set(
      data.tableData
        .filter(t => t.underlyingTicker && t.underlyingTicker !== '' && t.underlyingTicker !== t.ticker)
        .map(t => t.underlyingTicker as string)
    )

    // Find underlyings not already in our ticker list
    const missingUnderlyings = [...underlyings].filter(u => !tickers.includes(u))

    // Auto-add missing underlyings
    for (const underlying of missingUnderlyings) {
      // Skip if already loading
      if (loadingTickers.some(lt => lt.symbol === underlying)) continue

      // Add to ticker list and calculate
      setTickers(prev => {
        if (prev.includes(underlying)) return prev
        return [...prev, underlying]
      })
      calculateSingle({
        ticker: underlying,
        startDate,
        endDate,
        reinvestDividends: true,
        adjustInflation: settings.adjustInflation,
        initialInvestment: settings.initialInvestment,
      })
    }
  }, [settings.groupByUnderlying, data?.tableData, tickers, loadingTickers, startDate, endDate, settings.adjustInflation, settings.initialInvestment, setTickers, calculateSingle])

  // Handle ticker add callback - use incremental calculation
  const handleTickerAdd = useCallback((ticker: string) => {
    // Use single-ticker calculation for the new ticker
    calculateSingle({
      ticker,
      startDate,
      endDate,
      reinvestDividends: true,
      adjustInflation: settings.adjustInflation,
      initialInvestment: settings.initialInvestment,
    })
  }, [calculateSingle, startDate, endDate, settings.adjustInflation, settings.initialInvestment])

  // Handle ticker removal - also remove cached data
  const handleRemoveTicker = useCallback((ticker: string) => {
    setTickers(prev => prev.filter(t => t !== ticker))
    removeTickerData(ticker)
  }, [setTickers, removeTickerData])

  // Handle reset
  const handleReset = useCallback(() => {
    setTickers([])
    setStartDate(getDefaultStartDate())
    setEndDate(getTodayDate())
    setSettings(DEFAULT_SETTINGS)
    reset()
  }, [setTickers, setStartDate, setEndDate, setSettings, reset])

  // Handle row click to open chart modal
  const handleRowClick = useCallback((ticker: string) => {
    setSelectedTicker(ticker)
    setIsChartModalOpen(true)
  }, [])

  // Get chart data for selected ticker
  const selectedChartData = useMemo(() => {
    if (!selectedTicker || !data) return null
    return data.chartData.find(d => d.ticker === selectedTicker) || null
  }, [selectedTicker, data?.chartData])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Parameter Bar */}
      <div className="px-4 py-4">
        <ParameterBar
          tickers={tickers}
          onTickersChange={setTickers}
          onTickerAdd={handleTickerAdd}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          initialInvestment={settings.initialInvestment}
          onInitialInvestmentChange={(amount) => setSettings(prev => ({ ...prev, initialInvestment: amount }))}
          adjustInflation={settings.adjustInflation}
          onAdjustInflationChange={(value) => setSettings(prev => ({ ...prev, adjustInflation: value }))}
          groupByUnderlying={settings.groupByUnderlying}
          onGroupByUnderlyingChange={(value) => setSettings(prev => ({ ...prev, groupByUnderlying: value }))}
          onReset={handleReset}
          isCalculating={loading}
          columns={allColumns}
          visibleColumns={visibleColumns}
          onColumnToggle={toggleColumn}
          onColumnToggleAll={toggleAll}
        />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 px-4 pb-4">
        {/* Error Message */}
        {error && (
          <div
            className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
            role="alert"
          >
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div
            className="flex items-center justify-center py-12"
            role="status"
            aria-label="Calculating returns"
          >
            <svg
              className="animate-spin h-8 w-8 text-primary mr-3"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-muted-foreground">
              Calculating returns{tickers.length > 0 ? ` for ${tickers.join(', ')}` : ''}...
            </span>
          </div>
        )}

        {/* Results Table - show if we have data OR loading tickers */}
        {(data || loadingTickers.length > 0) && !loading && (
          <ReturnsTable
            data={data?.tableData ?? []}
            onRowClick={handleRowClick}
            chartData={data?.chartData}
            visibleColumns={visibleColumns}
            onRemoveTicker={handleRemoveTicker}
            loadingTickers={loadingTickers}
            groupByUnderlying={settings.groupByUnderlying}
            groupOrder={groupOrder}
            onReorderGroups={reorderGroups}
          />
        )}

        {/* Empty State - show only if no data, not loading, no error, and no loadingTickers */}
        {!data && !loading && !error && loadingTickers.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-muted-foreground text-center">
              Type a ticker symbol and press Enter to see returns.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border px-4 py-3">
        <p className="text-center text-xs text-muted-foreground">
          Data provided for informational purposes only. Past performance does not guarantee future results.
        </p>
      </footer>

      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => {
          setIsChartModalOpen(false)
          setSelectedTicker(null)
        }}
        ticker={selectedTicker || ''}
        chartData={selectedChartData}
        initialInvestment={settings.initialInvestment}
      />
    </div>
  )
}

export default Calculator
